import React from 'react';
import { Box, Grid, IconButton, TextField, Typography, InputAdornment, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { DroppableSection } from './DroppableSection';
import { FormLayout } from '../../types/form';
import { SortableFieldItem } from './SortableFieldItem';
import { Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon } from '@mui/icons-material';

// ============================================================================
// Types
// ============================================================================
type RelatedList = {
  id: string;
  tableName: string;
  label: string;
  fields: string[];
  relationshipField: string;
};

// ============================================================================
// Interfaces
// ============================================================================
interface SectionRendererProps {
  section: FormLayout['layout_config']['sections'][0];
  fields: FormLayout['layout_config']['fields'];
  editingSectionId: string | null;
  setEditingSectionId: (id: string | null) => void;
  onChangeTitle: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  onEditField: (field: FormLayout['layout_config']['fields'][0]) => void;
  relatedList?: RelatedList;
}

// ============================================================================
// Component
// ============================================================================
export const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  fields,
  editingSectionId,
  setEditingSectionId,
  onChangeTitle,
  onDelete,
  onEditField,
  relatedList
}) => {
  const isEditing = editingSectionId === section.id;
  const isRelatedList = section.type === 'related_list';

  return (
    <DroppableSection id={section.id}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {isEditing ? (
          <TextField
            value={section.title}
            onChange={(e) => onChangeTitle(section.id, e.target.value)}
            onBlur={() => setEditingSectionId(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChangeTitle(section.id, (e.target as HTMLInputElement).value);
                setEditingSectionId(null);
              } else if (e.key === 'Escape') {
                setEditingSectionId(null);
              }
            }}
            autoFocus
            size="small"
            sx={{ maxWidth: 300 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setEditingSectionId(null)}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        ) : (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            onClick={() => setEditingSectionId(section.id)}
            sx={{
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {section.title}
            <EditIcon sx={{ fontSize: 16, opacity: 0.6 }} />
          </Typography>
        )}
        <Box>
          <IconButton onClick={() => setEditingSectionId(section.id)} size="small" sx={{ mr: 1 }}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => onDelete(section.id)} size="small" disabled={section.id === 'default'}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {isRelatedList && relatedList ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Related table: {relatedList.tableName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fields shown:
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {relatedList.fields.map((field: string, index: number) => (
                  <TableCell key={field || `field-${index}`}>{field}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={relatedList.fields.length} align="center">
                  Related records will appear here
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <SortableFieldItem
              key={field.id || `field-${index}`}
              id={field.id}
              field={field}
              onEdit={() => onEditField(field)}
            />
          ))}
        </Grid>
      )}
    </DroppableSection>
  );
};
